/**
 * Thin client wrappers around the four ronin-api endpoints under
 * /api/stripeConnect. Each call attaches the bearer JWT from localStorage
 * (same key Apollo uses elsewhere: "ron-auth").
 */

import {
  OnboardingLinkResponse,
  LoginLinkResponse,
  StripeConnectStatus,
} from "./types";

// Fall back to https://api.roninpos.app so prod still works even if the
// env var isn't set in a given build.
const API_BASE =
  (import.meta.env.VITE_RONIN_API_BASE as string | undefined) ||
  "https://api.roninpos.app";

function authHeaders(): HeadersInit {
  const token = localStorage.getItem("ron-auth");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
  } catch (err: any) {
    // Network-level failure (no response) — usually CORS, DNS, or backend down.
    throw new Error(
      `Network error calling ${API_BASE}${path}: ${err?.message || err}. ` +
        `Check that ronin-api is running and CORS allows ${window.location.origin}.`
    );
  }
  if (!res.ok) {
    let message = `${res.status} ${res.statusText} from ${path}`;
    try {
      const data = await res.json();
      if (data?.error) message = `${res.status}: ${data.error}`;
      else if (data?.message) message = `${res.status}: ${data.message}`;
    } catch {
      // Body wasn't JSON. Include a hint for the most common case.
      if (res.status === 404) {
        message += ` — endpoint not found. Is the ronin-api stripeConnect branch deployed?`;
      }
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export function createOnboardingLink(
  processorId: number,
  returnUrl: string,
  refreshUrl: string,
  /**
   * Optional Stripe mode override for the new connected account. Only the
   * backend will honor it if the caller is admin; non-admin callers always
   * get the live key regardless.
   */
  mode?: "test" | "live"
): Promise<OnboardingLinkResponse> {
  return postJson<OnboardingLinkResponse>(
    "/api/stripeConnect/createOnboardingLink",
    mode ? { processorId, returnUrl, refreshUrl, mode } : { processorId, returnUrl, refreshUrl }
  );
}

/**
 * "New connection" variant of createOnboardingLink: no processor row exists
 * yet. The backend creates the row, then a PLATFORM-CONTROLLED Stripe account
 * (controller.is_controller=true — required for Apps on Devices to deploy the
 * Ronin POS app to the merchant's readers), then returns a Stripe-hosted
 * onboarding URL. The merchant experience matches the old OAuth signup: they
 * end up with their own stripe.com login, the full Stripe dashboard, they pay
 * their own Stripe fees and bear their own losses.
 *
 * returnUrl/refreshUrl may contain a literal "{processorId}" placeholder; the
 * backend interpolates the new row's id into it.
 */
export function createOnboardingLinkForNew(params: {
  organizationId: number;
  vendorId: number;
  descriptor: string;
  returnUrl: string;
  refreshUrl: string;
  userId?: number;
  mode?: "test" | "live";
}): Promise<OnboardingLinkResponse & { processorId: number }> {
  return postJson("/api/stripeConnect/createOnboardingLink", params);
}

export function refreshAccountStatus(
  processorId: number
): Promise<StripeConnectStatus> {
  return postJson<StripeConnectStatus>(
    "/api/stripeConnect/refreshAccountStatus",
    { processorId }
  );
}

/**
 * Bulk status sync — refreshes many rows from Stripe in one call so the grid
 * is truthful on page load instead of ops clicking into every panel.
 * scope 'stale' (default) touches only rows that aren't fully onboarded (or
 * are missing the deployability flag); 'all' re-pulls every connected row.
 * Non-admin callers are scoped server-side to their own organization.
 */
export function refreshAllAccountStatuses(
  scope: "stale" | "all" = "stale"
): Promise<{
  scope: string;
  scanned: number;
  refreshed: number;
  changed: number;
  errors: Array<{ id: number; error: string }>;
}> {
  return postJson("/api/stripeConnect/refreshAllAccountStatuses", { scope });
}

export function createLoginLink(
  processorId: number
): Promise<LoginLinkResponse> {
  return postJson<LoginLinkResponse>(
    "/api/stripeConnect/createLoginLink",
    { processorId }
  );
}

export function disconnectAccount(
  processorId: number
): Promise<StripeConnectStatus> {
  return postJson<StripeConnectStatus>(
    "/api/stripeConnect/disconnectAccount",
    { processorId }
  );
}

export function relinkPreviousAccount(
  processorId: number
): Promise<StripeConnectStatus> {
  return postJson<StripeConnectStatus>(
    "/api/stripeConnect/relinkPreviousAccount",
    { processorId }
  );
}

/**
 * HARD unlink: revokes Ronin's platform access to the connected account at
 * Stripe (oauth.deauthorize), then VERIFIES severance by confirming the
 * platform can no longer retrieve the account. `verified: false` means the
 * link is still live (e.g. a platform-created Express account) and nothing
 * was scrubbed — do not delete the row in that case.
 */
export function deauthorizeAccount(processorId: number): Promise<
  Partial<StripeConnectStatus> & {
    deauthorized: boolean;
    verified: boolean;
    accountId: string | null;
    detail?: string;
  }
> {
  return postJson("/api/stripeConnect/deauthorizeAccount", { processorId });
}

export interface TerminalLocationAddress {
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postal_code: string;
  country: string;
}

export interface TerminalLocationResponse {
  id: string;
  displayName: string;
  address: any;
}

export interface RelocateResult {
  moved: boolean;
  fromLocationId: string;
  locationId: string;
  locationDisplayName: string;
  addressSource: string;
  oldLocationRemoved: boolean;
  oldLocationRemoveError?: string;
}

/**
 * Move a venue's Terminal location from the merchant's account to Ronin's,
 * carrying the original address across and removing the old one.
 *
 * `address` is optional: the backend copies the existing location's address
 * unless a COMPLETE one is supplied, so a half-filled form can never silently
 * replace an address that prints on receipts.
 */
export function relocateTerminalLocationToPlatform(
  processorId: number,
  displayName?: string,
  address?: Partial<TerminalLocationAddress>
): Promise<RelocateResult> {
  return postJson<RelocateResult>(
    "/api/stripeConnect/relocateTerminalLocationToPlatform",
    { processorId, displayName, address }
  );
}

/**
 * Record a venue as running through Ronin when its Terminal location is
 * ALREADY on Ronin's platform account — common across the legacy fleet, where
 * only the config keys are missing. Creates and deletes nothing.
 */
export function adoptPlatformTerminalLocation(
  processorId: number
): Promise<{ adopted: boolean; locationId: string; reason: string }> {
  return postJson("/api/stripeConnect/adoptPlatformTerminalLocation", {
    processorId,
  });
}

export interface TerminalLocationWhereabouts {
  /** Which Stripe account the recorded location actually lives on. */
  account: "platform" | "connected" | "missing" | "none" | "unknown";
  locationId: string | null;
  connectedAccountId?: string;
  displayName?: string | null;
  /** What the row CLAIMS, for comparison with where it actually is. */
  recordedAsDestination?: boolean;
  /**
   * Where the location SHOULD be, decided by the same rule setupVenueTerminal
   * uses — so the two can never disagree. "platform" means this account is
   * fee-locked and/or not platform-controlled, so its charges have to run
   * through Ronin.
   */
  expected?: "platform" | "connected" | "unknown";
  expectedReason?: string | null;
  error?: string;
}

/**
 * Ask Stripe which account this venue's Terminal location is on. Config alone
 * cannot answer it: `platform_account_location_id` means "where the readers
 * live" and that is a merchant-account location for some rows and a platform
 * location for others, with nothing in the data distinguishing the legacy
 * ones. The backend probes both accounts.
 */
export function whereIsTerminalLocation(
  processorId: number
): Promise<TerminalLocationWhereabouts> {
  return postJson<TerminalLocationWhereabouts>(
    "/api/stripeConnect/whereIsTerminalLocation",
    { processorId }
  );
}

export interface VenueSetupResult {
  placement: "platform" | "connected";
  reason: string;
  locationId: string;
  locationDisplayName: string;
  configurationId: string | null;
  deployGroup: {
    /** null for a platform location — there is no account to search. */
    accountToSearch: string | null;
    locationId: string;
    locationDisplayName: string;
    onPlatform: boolean;
  };
  chargeFlowEnabled: boolean;
}

/**
 * Create this venue's Terminal location on whichever account it belongs to.
 * The backend decides — ops never picks. See setupVenueTerminal in
 * ronin-api/api/stripeConnect/[action].js for the rule.
 *
 * This does NOT move the venue's online revenue; see setDestinationChargeFlow.
 */
export function setupVenueTerminal(
  processorId: number,
  displayName: string,
  address: TerminalLocationAddress
): Promise<VenueSetupResult> {
  return postJson<VenueSetupResult>("/api/stripeConnect/setupVenueTerminal", {
    processorId,
    displayName,
    address,
  });
}

/**
 * Move (or return) the venue's CARD-NOT-PRESENT revenue — ticketing, order
 * ahead, invoicing, top-ups. Card-present already follows wherever the readers
 * were set up. Admin only.
 */
export function setDestinationChargeFlow(
  processorId: number,
  enabled: boolean
): Promise<{ chargeFlow: "destination" | "direct" }> {
  return postJson("/api/stripeConnect/setDestinationChargeFlow", {
    processorId,
    enabled,
  });
}

/** Reverse a setup that has no readers registered against it yet. */
export function undoVenueSetup(
  processorId: number
): Promise<{ undone: boolean; removedLocationId: string }> {
  return postJson("/api/stripeConnect/undoVenueSetup", { processorId });
}

export function upsertTerminalLocation(
  processorId: number,
  displayName: string,
  address: TerminalLocationAddress
): Promise<TerminalLocationResponse> {
  return postJson<TerminalLocationResponse>(
    "/api/stripeConnect/upsertTerminalLocation",
    { processorId, displayName, address }
  );
}

/**
 * RETIRED (Aug 2026) — the backend now answers 410 Gone. OAuth-linked
 * accounts have controller.is_controller=false, and Stripe refuses to deploy
 * the Ronin POS app to readers on accounts the platform doesn't control, so
 * every OAuth-linked merchant's devices boot to the Stripe splash screen.
 * Use createOnboardingLinkForNew / createOnboardingLink instead. Kept only
 * so stale callers fail loudly with the backend's explanation rather than a
 * compile break; completeOAuthLink below stays functional for in-flight
 * authorizations.
 *
 * @param signupHint  "register" sends merchants to Stripe's signup screen
 *                    ("I don't have an account yet"); "login" (the default)
 *                    sends them to the login screen ("I already have an
 *                    account").
 */
export function createOAuthLink(
  processorId: number,
  returnUrl: string,
  mode?: "test" | "live",
  signupHint?: "login" | "register"
): Promise<{ url: string; mode: "test" | "live" }> {
  const body: Record<string, unknown> = { processorId, returnUrl };
  if (mode) body.mode = mode;
  if (signupHint) body.signupHint = signupHint;
  return postJson("/api/stripeConnect/createOAuthLink", body);
}

/**
 * Complete the OAuth Standard flow by exchanging the code Stripe returned
 * in the redirect query string for an access token + connected account id.
 */
export function completeOAuthLink(
  processorId: number,
  code: string,
  state?: string
): Promise<StripeConnectStatus & { linkType: "oauth" }> {
  return postJson(
    "/api/stripeConnect/completeOAuthLink",
    { processorId, code, state }
  );
}

/**
 * RETIRED (Aug 2026) — see createOAuthLink above; the backend answers 410
 * Gone. Use createOnboardingLinkForNew instead.
 */
export function createOAuthLinkForNew(params: {
  organizationId: number;
  vendorId: number;
  descriptor: string;
  businessName?: string;
  returnUrl: string;
  userId?: number;
  mode?: "test" | "live";
  signupHint?: "login" | "register";
}): Promise<{ url: string; mode: "test" | "live" }> {
  const body: Record<string, unknown> = {
    organizationId: params.organizationId,
    vendorId: params.vendorId,
    descriptor: params.descriptor,
    returnUrl: params.returnUrl,
  };
  if (params.businessName) body.businessName = params.businessName;
  if (params.userId != null) body.userId = params.userId;
  if (params.mode) body.mode = params.mode;
  if (params.signupHint) body.signupHint = params.signupHint;
  return postJson("/api/stripeConnect/createOAuthLink", body);
}

/**
 * Complete a "new connection" OAuth return. No processorId — the backend
 * decodes the pending vendor/org from the `new:` state and creates the row.
 */
export function completeOAuthLinkForNew(
  code: string,
  state: string
): Promise<
  StripeConnectStatus & { linkType: "oauth"; processorId: number | null }
> {
  return postJson("/api/stripeConnect/completeOAuthLink", { code, state });
}

// --- Terminal cutover (destination charge flow) ----------------------------

export interface MigrateReaderResult {
  serial?: string;
  readerId?: string;
  ok: boolean;
  skipped?: boolean;
  bluetoothSkipped?: boolean;
  note?: string;
  error?: string;
  oldReaderId?: string;
  newReaderId?: string;
}

export interface MigrateTerminalResponse {
  migrated: boolean;
  platformLocationId: string;
  platformConfigurationId: string | null;
  readers: MigrateReaderResult[];
  warning?: string;
}

export interface RollbackTerminalResponse {
  rolledBack: boolean;
  readers: MigrateReaderResult[];
  warning?: string;
}

/**
 * Move a venue's Terminal setup (location, configuration, smart readers) onto
 * RONIN's platform account and record the destination-flow cutover. Safe by
 * construction: canary ordering stops at the first failed reader, Bluetooth
 * readers are skipped (they follow the app session automatically), and the
 * cutover key is only written when every reader made it. Reader-less venues
 * need force=true. registrationCodes ({serial: code}) is the fallback if
 * Stripe rejects serial-number registration.
 */
export function migrateTerminalToPlatform(
  processorId: number,
  opts?: { force?: boolean; registrationCodes?: Record<string, string> }
): Promise<MigrateTerminalResponse> {
  return postJson<MigrateTerminalResponse>(
    "/api/stripeConnect/migrateTerminalToPlatform",
    { processorId, ...(opts?.force ? { force: true } : {}), ...(opts?.registrationCodes ? { registrationCodes: opts.registrationCodes } : {}) }
  );
}

/** Reverse a cutover: readers back to the merchant account, tokens flip back immediately. */
export function rollbackTerminalToConnected(
  processorId: number,
  opts?: { registrationCodes?: Record<string, string> }
): Promise<RollbackTerminalResponse> {
  return postJson<RollbackTerminalResponse>(
    "/api/stripeConnect/rollbackTerminalToConnected",
    { processorId, ...(opts?.registrationCodes ? { registrationCodes: opts.registrationCodes } : {}) }
  );
}
